import React, { useEffect, useState } from 'react';

import {
    Drawer,
    Descriptions,
    Typography,
    Tag,
    Spin,
    message,
    Checkbox,
    GetProp,
    CollapseProps,
    Collapse,
} from 'antd';
// import { DownOutlined } from '@ant-design/icons';

import { formatDate } from '../../utils/format';
import useGetAdminById from '../../hooks/admin-management/useGetAdminById';
import { RenderIf } from '../RenderIf';
// import useUpdateAdminStatus from '../../hooks/admin-management/useUpdateAdminStatus';
import useAssignAdminRole from '@/hooks/admin-management/useAssignAdminRole';
import useGetAdminRoles from '@/hooks/admin-management/useGetAdminRoles';
import {
    RoleSchema,
    TAssignAdminRolePayload,
    TUpdateAdminRolePayload,
} from '@/types/admin-management/admin-roles-types';
import useUpdateAdminRole from '@/hooks/admin-management/useUpdateAdminRole';
import useRemoveAdminRole from '@/hooks/admin-management/useRemoveAdminRole';
import { useRootStore } from '@/store/root-store';
import { doesUserHaveAccess } from '@/utils/permissions';
import { PERMISSION_NAMES } from '@/constants/permissions';

const { Title } = Typography;

type TAdminProfileDrawerProp = {
    visible: boolean;
    onClose: () => void;
    user: any; //TODO: change this
    userType: string;
};

const AdminProfileDrawer = ({
    visible,
    onClose,
    user,
    userType,
}: TAdminProfileDrawerProp) => {
    const { permissions, isUserSuperAdmin, roles } = useRootStore();
    const { data, isLoading } = useGetAdminById(
        user?.id,
        userType,
        (Array.isArray(permissions) && permissions.length > 0 && doesUserHaveAccess(permissions, PERMISSION_NAMES.GET_ADMIN_USER)) ||
            isUserSuperAdmin
    );

    // const { mutate, isLoading: isMutateLoading } = useUpdateAdminStatus();

    // Update roles
    const {
        mutate: mutateUpdateAdminRole,
        isPending: isUpdateAdminRolePending,
    } = useAssignAdminRole();

    const {
        isPending: isRemoveAdminRolePending,
        mutate: removeAdminRoleMutate,
    } = useRemoveAdminRole();

    const { data: adminRoles, isPending } = useGetAdminRoles({});

    const hasRole = data?.user_roles?.length > 0;

    const [checkedList, setCheckedList] = useState<string[]>([]);

    // Set selectedRoles
    useEffect(() => {
        if (data?.user_roles && Array.isArray(data?.user_roles)) {
            setCheckedList(data?.user_roles.map((role) => role.role_id));
        }
    }, [data?.user_roles]);

    const handleAdminRoleUpdate = (roleID: string) => {
        if (
            (Array.isArray(permissions) && permissions.length > 0 && doesUserHaveAccess(permissions, PERMISSION_NAMES.GET_ADMIN_USER)) ||
            isUserSuperAdmin
        ) {
            // If user has a role, call update and not create
            const payload: TAssignAdminRolePayload = {
                role_id: roleID,
                user_id: user.id,
            };
            mutateUpdateAdminRole(payload);
            // if (hasRole) {
            //     const adminRoleIdToAdd = adminRoles?.filter(
            //         (role) => role.id === roleID
            //     );
            //     console.log('adminRoleIdToAdd', adminRoleIdToAdd);

            //     // TODO: send proper ID
            //     const payload: TUpdateAdminRolePayload & { id: string } = {
            //         role_id: roleID,
            //         expiration_timestamp: 2526249600000, // Jan 20 2050 - change this to another timestamp
            //         id: adminRoleIdToAdd[0].id, // admin role id
            //     };
            //     updateAdminRoleMutate(payload);
            // } else {
            //     const payload: TAssignAdminRolePayload = {
            //         role_id: roleID,
            //         user_id: user.id,
            //     };
            //     mutateUpdateAdminRole(payload);
            // }
        } else {
            // USER does not have access
        }
    };

    const renderAdminInfo = () => {
        if (!data || userType !== 'admin') return null;

        const userStatus = (data || user)?.status || 'new';
        const userAdminStatus = (data || user)?.role?.role_name;

        // Create status tag (read-only, no interactive functionality)
        const StatusTag = () => {
            const tagColors = {
                approved: 'success',
                new: 'warning',
                declined: 'error',
                default: 'default',
            };

            const tagColor = tagColors[userStatus] || tagColors.default;
            const statusText = userStatus.toUpperCase() || 'N/A';

            // Show status as read-only tag
            return <Tag color={tagColor}>{statusText}</Tag>;
        };

        const AdminRoleTag = () => {
            const getRoleColor = (roleName) => {
                if (roleName === 'SUPER_ADMIN' || roleName === 'Super Administrator') {
                    return 'magenta';
                } else if (roleName === 'ADMIN' || roleName === 'Administrator') {
                    return 'cyan';
                }
                return 'default';
            };

            return (
                <Tag
                    color={getRoleColor(data.role?.role_name)}
                >
                    {data.role?.role_name || 'ADMIN'}
                </Tag>
            );
        };

        const onCheckBoxChange: GetProp<typeof Checkbox.Group, 'onChange'> = (
            checkedValues
        ) => {
            console.log('checkedValues', checkedValues);
            const initialSelectedRoles = (data?.user_roles ?? [])?.map(
                (role) => role.role_id
            );

            if ((data?.user_roles ?? []).length < checkedValues.length) {
                // means something was added
                const added = checkedValues.filter(
                    (item) => !initialSelectedRoles.includes(item as string)
                );
                console.log('added role id', added);
                handleAdminRoleUpdate(added[0] as string);
            } else {
                const removed = initialSelectedRoles.filter(
                    (item) => !checkedValues.includes(item)
                );
                const adminRoleIdToRemove = (data?.user_roles ?? [])?.filter(
                    (role) => role.role_id === removed[0]
                );
                removeAdminRoleMutate(adminRoleIdToRemove[0].id);
            }

            setCheckedList(checkedValues as string[]);
        };

        const rolesOptions = (adminRoles ?? []).map((role) => ({
            ...role,
            label: role.display_name,
            value: role?.id,
        }));

        const items: CollapseProps['items'] = [
            {
                key: '1',
                label: 'Select Admin Roles',
                children: (
                    <Checkbox.Group
                        disabled={
                            isRemoveAdminRolePending || isUpdateAdminRolePending
                        }
                        options={rolesOptions}
                        // defaultValue={checkedList}
                        onChange={onCheckBoxChange}
                        value={checkedList}
                    />
                ),
            },
        ];

        const onToggle = (key: string | string[]) => {
            console.log(key);
        };

        return (
            <>
                <Descriptions.Item label="ID">
                    {data?.id || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="First Name">
                    {data.firstname || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Last Name">
                    {data.lastname || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                    {data.email || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone Number">
                    {formatPhoneNumber(data.phone_number) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                    <StatusTag />
                </Descriptions.Item>
                <Descriptions.Item label="Is Activated">
                    <Tag color={data.is_activated ? 'success' : 'error'}>
                        {data.is_activated ? 'YES' : 'NO'}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item
                    label="Role"
                    className="description-no-padding"
                >
                    <AdminRoleTag />

                    <Collapse
                        items={items}
                        defaultActiveKey={['1']}
                        onChange={onToggle}
                        bordered={false}
                        style={{ borderRadius: '0px !important' }}
                    />
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                    {formatCreatedDate(data.created) || 'N/A'}
                </Descriptions.Item>
            </>
        );
    };

    // Status change is now handled in the three-dot menu of AdminUsers component
    // const handleStatusChange = async (userId, newStatus) => {
    //     // TODO: Update this when I refactor for individual and enterprise
    //     if (userType === 'admin') {
    //         mutate(
    //             { status: newStatus, id: userId },
    //             {
    //                 onSuccess: () => {
    //                     message.success(`Admin status updated to ${newStatus}`);
    //     });
    //     }
    // };

    const handleUpdateAdminRole = async () => {
        // mutateUpdateAdminRole()
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return 'N/A';
        // Remove any non-digit characters except + for international format
        const cleaned = phone.replace(/[^\d+]/g, '');
        if (!cleaned) return 'N/A';

        // Handle international format
        if (cleaned.startsWith('+')) {
            return phone;
        }

        // Format as local number if exactly 10 digits
        if (cleaned.length === 10) {
            return cleaned.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
        }

        // Return as is for other formats
        return phone;
    };

    // Function to safely format the created date
    const formatCreatedDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        // Convert to number if it's a string
        if (typeof timestamp === 'string') {
            timestamp = parseInt(timestamp, 10);
        }

        // Ensure it's a valid number
        if (isNaN(timestamp)) return 'N/A';

        // If timestamp is in seconds (Unix timestamp), convert to milliseconds
        // Most Unix timestamps are 10 digits (seconds), while JS timestamps are 13 digits (milliseconds)
        if (timestamp < 10000000000) {
            timestamp *= 1000;
        }

        try {
            // Use the formatDate function with a specific format
            return formatDate(timestamp, 'DD/MM/YYYY HH:mm:ss');
        } catch (e) {
            console.error('Error formatting date:', e, timestamp);
            return 'N/A';
        }
    };

    // Determine the user's name based on type and available fields
    const getUserName = () => {
        if (!user && !data) return 'User';

        if (userType === 'individual') {
            const firstName = (data ?? user).firstname || '';
            const lastName = (data ?? user).lastname || '';
            if (firstName || lastName) {
                return `${firstName} ${lastName}`.trim();
            }
        }

        return (data ?? user).name || (data ?? user).business_name || 'User';
    };

    return (
        <Drawer
            title={`${getUserName()} Profile`}
            placement="right"
            onClose={onClose}
            open={visible}
            width={500}
        >
            <RenderIf condition={isLoading}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <Spin size="large" />
                </div>
            </RenderIf>
            <RenderIf condition={!isLoading && !!data}>
                {data || user ? (
                    <>
                        <Title level={4}>{getUserName()}</Title>
                        <Descriptions
                            bordered
                            column={1}
                        >
                            {userType === 'admin' && renderAdminInfo()}
                        </Descriptions>
                    </>
                ) : (
                    <div>No user data available</div>
                )}
            </RenderIf>
        </Drawer>
    );
};

export default AdminProfileDrawer;
